import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';

import {
  EGenplanControlsType,
  trackGenplanControlsClick,
  trackGenplanFlatsOnFloorClick,
  trackHouseHover,
  trackHouseLinkClick,
  trackShowGenplan,
} from './tracking';

import { Genplan } from '../../components/Genplan';
import { House } from '../../components/Genplan/components/House';
import { HouseTooltip } from '../../components/Genplan/components/HouseTooltip';
import { GenplanControls } from '../../components/GenplanControls';

import { genplanSelector, isFloorPlanAvailableSelector, newbuildingIdSelector } from '../../selectors/newbuilding';
import { setFloorPlanHouseId, setOpenFloorPlan } from '../../slices';

/* istanbul ignore next */
export function GenplanContainer(): React.JSX.Element | null {
  const dispatch = useDispatch();

  const genplan = useSelector(genplanSelector);
  const newbuildingId = useSelector(newbuildingIdSelector);
  const isFloorPlanAvailable = useSelector(isFloorPlanAvailableSelector);
  const [viewedHouses, setViewedHouses] = React.useState<number[]>([]);

  const onHover = React.useCallback(
    (houseId: number) => {
      /* istanbul ignore else */
      if (!viewedHouses.includes(houseId)) {
        trackHouseHover(newbuildingId, houseId);
        setViewedHouses([...viewedHouses, houseId]);
      }
    },
    [newbuildingId, viewedHouses],
  );

  const onHouseLinkClick = React.useCallback(
    (houseId: number, roomType: string) => {
      trackHouseLinkClick(newbuildingId, houseId, roomType);
    },
    [newbuildingId],
  );

  if (!genplan) {
    return null;
  }

  const onFloorPlanClick = (inTooltip?: boolean, houseId?: number) => () => {
    dispatch(setFloorPlanHouseId(houseId || null));
    dispatch(setOpenFloorPlan(true));

    if (inTooltip) {
      trackGenplanFlatsOnFloorClick(newbuildingId, houseId);
    } else {
      trackGenplanControlsClick(newbuildingId, EGenplanControlsType.Floor);
    }
  };

  const { image, isHousesNamesEnabled, houses } = genplan;

  return (
    <Genplan
      image={image}
      onShow={trackShowGenplan}
      renderHouses={(containerWidth: number, containerHeight: number) =>
        houses.map(({ name, houseInfo, stats, isReliable, isEscrow, isSalesStart, ...house }) => (
          <House
            key={house.id}
            {...house}
            name={name}
            isReliable={isReliable}
            isEscrow={!!isEscrow}
            isSalesStart={isSalesStart}
            emptyStats={!stats}
            showTitle={isHousesNamesEnabled}
            containerWidth={containerWidth}
            containerHeight={containerHeight}
            tooltip={
              <HouseTooltip
                isFloorPlanEnabled={isFloorPlanAvailable}
                name={name}
                houseId={house.id}
                stats={stats}
                houseInfo={houseInfo}
                isReliable={isReliable}
                isEscrow={!!isEscrow}
                isSalesStart={isSalesStart}
                onLinkClick={onHouseLinkClick}
                onFloorPlanClick={onFloorPlanClick(true, house.id)}
              />
            }
            onHover={() => onHover(house.id)}
          />
        ))
      }
    >
      <GenplanControls isFloorPlanAvailable={isFloorPlanAvailable} onFloorPlanClick={onFloorPlanClick()} />
    </Genplan>
  );
}
